import { Flex, Tag, Tooltip, Typography } from "antd";
import { ColumnType } from "antd/es/table";
import { ReactComponent as ArrowIcon } from "images/arrow-right.svg";
import { ReactComponent as ClockIcon } from "images/other/clock.svg";

import { ReactComponent as ShieldIcon } from "images/headerIcons/shieldCheck.svg";
import { ReactComponent as ShieldPlusIcon } from "images/headerIcons/shieldPlus.svg";
import {
  BID_REQUEST_STATUS_ENUM,
  BidEntity,
  QUOTE_SELECTION_ENUM,
  QuoteEntity,
  QuoteStatus,
  RISK_LEVEL_COLORS,
  RISK_LEVELS,
  RISK_STATUS_COLORS,
  RISK_STATUS_ENUM,
  RISK_STATUS_TEXTS,
  RiskStatus,
  WalletRiskLevel,
} from "interfaces";
import { CryptoIcon } from "ui";
import { dateFormat, formatWithDecimals, isStatusIncluded } from "utils";
import { UserStore } from "stores/UserStore";
import { ReactComponent as RubIcon } from "images/coins/rubLargeIconRound.svg";

import styles from "./quotes.module.css";
import { QuoteList } from "stores/QuotesList";
import { QUOTE_SELECTION } from "utils/envConstants";

import { RiskTextColor } from "components";
import { QuoteExpTime } from "./QuoteExpTime";
import { DetailReportButton } from "pages/AmlChecksPage/DetailReport";

export const getColumns = (user: UserStore, data: BidEntity, quotes: QuoteList): Array<ColumnType<QuoteEntity>> => {
  const { isProvider, isSeniorLP, isSeniorOperator, isJuniorOperator, isRiskManager, isCustomer } = user.roles;

  const isAnyOperator = isSeniorOperator || isJuniorOperator || isRiskManager;

  const showNativeBalanceForCustomer = QUOTE_SELECTION === QUOTE_SELECTION_ENUM.CUSTOMER && isCustomer;
  const shouldShowQuoteExpTime = !isStatusIncluded(data.status, [
    BID_REQUEST_STATUS_ENUM.FUNDING,
    BID_REQUEST_STATUS_ENUM.AML_CHECK,
    BID_REQUEST_STATUS_ENUM.AML_FAILED,
    BID_REQUEST_STATUS_ENUM.AML_FAILED_REFUNDING,
    BID_REQUEST_STATUS_ENUM.AML_FAILED_REFUNDED,
    BID_REQUEST_STATUS_ENUM.FUNDED,
    BID_REQUEST_STATUS_ENUM.EXECUTION,
    BID_REQUEST_STATUS_ENUM.COMPLETED,
    BID_REQUEST_STATUS_ENUM.CANCELED_REFUNDING,
    BID_REQUEST_STATUS_ENUM.CANCELED_REFUNDED,
    BID_REQUEST_STATUS_ENUM.CANCELED,
  ]);

  const columns = [
    {
      dataIndex: "rate",
      key: "rate",
      width: 350,
      align: "right",
      render: (_: any, record: QuoteEntity) => {
        return (
          <div className={styles.rateCell}>
            <Flex gap="small" align="center">
              <Flex align="center" gap="small">
                {data.currency && <CryptoIcon currency={data.currency} squareSize="3xl" />}

                <Flex vertical>
                  <Tooltip title={`Курс 1 ${data.currency} в рублях`}>
                    <span style={{ fontWeight: "bold" }}>{formatWithDecimals(record.rate)}</span>
                  </Tooltip>
                  <Typography.Text className="text-xs text-description">RUB/{data.currency}</Typography.Text>
                </Flex>
              </Flex>

              {
                <>
                  →
                  <Flex align="center" gap="small">
                    {data.network && <RubIcon style={{ width: "24px", height: "24px" }} />}
                    <Flex vertical>
                      <Tooltip title={`Сумма заявки в ${data.currency}`}>
                        <Typography.Text className="text-description text-xs">
                          {formatWithDecimals(data.amount)} {data.currency}
                        </Typography.Text>
                      </Tooltip>
                      <Flex vertical>
                        <Tooltip title={`Сумма заявки в рублях`}>
                          <span style={{ fontWeight: "bold", color: "var(--ant-color-primary)" }}>
                            {formatWithDecimals(data.amount ? data.amount * record.rate : 0)}
                          </span>
                        </Tooltip>
                        <Typography.Text className="text-xs text-description">Сумма RUB</Typography.Text>
                      </Flex>
                    </Flex>
                  </Flex>
                </>
              }
            </Flex>
          </div>
        );
      },
    },

    !showNativeBalanceForCustomer && {
      dataIndex: "feeCurRate",
      key: "feeCurRate",
      width: 170,
      title: (
        <Tooltip trigger="hover" placement="top" title={`Котировка БЧ комиссия руб. за 1 ${data.network}`}>
          {data.network}/RUB
        </Tooltip>
      ),
      align: "right",
      render: (_: any, record: QuoteEntity) => {
        return (
          <Flex align="center" gap="small">
            {data.network && <CryptoIcon currency={data.network} squareBg squareSize="3xl" ethIconRound />}
            <Flex vertical>
              <Tooltip title={`БЧ комиссия руб. за 1 ${data.network}`}>
                <span style={{ fontWeight: "bold" }}>{formatWithDecimals(record.feeCurRate)}</span>
              </Tooltip>

              <Typography.Text className="text-xs text-description">{data.network}/RUB</Typography.Text>
            </Flex>
          </Flex>
        );
      },
    },

    shouldShowQuoteExpTime && {
      dataIndex: "quoteExpTime",
      key: "quoteExpTime",
      title: "Действительна до",
      align: "right",
      width: 200,
      render: (_: any, record: QuoteEntity) => {
        return (
          <Flex align="start" gap={2}>
            <ClockIcon style={{ width: "20px", height: "20px" }} />
            <Flex gap="small" vertical>
              <Flex vertical>
                <span>{record.quoteExpTime ? `до ${dateFormat(record.quoteExpTime, true)}` : "Бессрочно"}</span>
                <QuoteExpTime quoteExpTime={record.quoteExpTime ?? ""} />
              </Flex>
            </Flex>
          </Flex>
        );
      },
    },
    isAnyOperator && {
      dataIndex: "riskLevel",
      key: "riskLevels",
      title: "Уровень риска",
      width: 170,
      align: "left",
      render: (riskLevel: WalletRiskLevel) => {
        const color = riskLevel ? RISK_LEVEL_COLORS[riskLevel] : "default";
        return (
          <Flex align="center" gap="small">
            <ShieldIcon style={{ width: "20px", height: "20px" }} className={styles[color]} />
            <Flex vertical align="start">
              {riskLevel ? (
                <RiskTextColor type={RISK_LEVEL_COLORS[riskLevel]} text={RISK_LEVELS.texts[riskLevel]} />
              ) : (
                <RiskTextColor type="default" text="Не проверялась" />
              )}
              <span className="text-description text-xs">Уровень риска</span>
            </Flex>
          </Flex>
        );
      },
    },
    isAnyOperator && {
      dataIndex: "riskStatus",
      key: "riskStatusList",
      title: "Статус проверки",
      width: 170,
      align: "left",
      render: (_: any, record: QuoteEntity) => {
        const color = record.riskStatus ? RISK_STATUS_COLORS[record.riskStatus] : "warning";
        if (record.riskStatus) {
          const approveNeeded = record.riskStatus === RISK_STATUS_ENUM.APPROVE_NEEDED;
          return (
            <Flex align="center" gap="small">
              <ShieldPlusIcon style={{ width: "20px", height: "20px" }} className={styles[color]} />
              <span>
                <Flex vertical align="start">
                  <div>
                    {approveNeeded ? (
                      <RiskTextColor type="warning" text="Требует согласования" />
                    ) : (
                      <RiskTextColor
                        type={RISK_STATUS_COLORS[record.riskStatus]}
                        text={RISK_STATUS_TEXTS[record.riskStatus]}
                      />
                    )}
                  </div>
                  <span className="text-description text-xs">Статус проверки</span>
                </Flex>
              </span>
            </Flex>
          );
        }

        return null;
      },
    },
    isRiskManager && {
      dataIndex: "checkId",
      key: "checkId",
      title: "Детали проверки",
      width: 50,
      className: "text-align-right justify-self-center",

      render: (_: any, record: QuoteEntity) => {
        return record.checkId ? <DetailReportButton checkId={record.checkId} type="wallet" /> : null;
      },
    },
  ].filter((c) => !!c) as ColumnType<QuoteEntity>[];

  const columnCount = columns.length;
  if (columns[0]) {
    columns[0].width = columnCount <= 3 ? 200 : 350;
  }

  return columns;
};
